import styled, { keyframes } from 'styled-components'
import _ from 'lodash'
import html2canvas from 'html2canvas'
import downloadjs from 'downloadjs'
import { useRef } from 'react'
import PlanetImage from './PlanetImage'
import ScanReadout from './ScanReadout'
import Scanner from './Scanner'
import SystemMonitor from './SystemMonitor'
import BlockadeText from './BlockadeText'
import { DefaultButton } from "./DefaultButton"
import { DataRow, PoiRow, WardenRow, HiddenRow, TypeRow } from './rows'
import { blockadeIcon } from "./icons"

const LayoutContainer = styled.div`
  color: ${(props) => props.theme.primaryColor};
  background-color: ${(props) => props.theme.backgroundColor};
  transition: background-color 0.1s ease, color 0.1s ease;
`

const Header = styled.header`
  border-bottom: 1px solid ${(props) => props.theme.primaryColor};
`

const HeaderContainer = styled.div`
  max-width: var(--max-width);
  display: flex;
  gap: 1em;
  justify-content: space-between;
  padding: 0 var(--main-padding);
  margin: 0 auto;

  @media (max-width: 1400px) {
    padding: 0 2em;
  }

  @media (max-width: 875px) {
    padding: 0 1em;
    flex-direction: column;
  }
`

const TitleContainer = styled.div`
  display: flex;
  width: 50%; 
  justify-content: space-between;
    
  @media (max-width: 875px) {
    width: 100%;
  }
`

const MainTitle = styled.h1`
  font-size: 2rem;
  margin: auto 0;

  @media (max-width: 875px) {
    margin-top: .5em;
  }
`

const VersionButton = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: ${(props) => props.theme.primaryColor};
  margin: auto 0;
  margin-right: .6em;
  text-decoration: underline;

  :hover {
    cursor: crosshair;
    background-color: ${(props) => props.theme.hoverColor};
  }

  :focus {
    outline: 1px solid ${(props) => props.theme.hoverColor};
  }

  @media (max-width: 875px) {
    margin-top: 2em;
    margin-right: 0;
    padding-bottom: .2em;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  width: 50%;
  gap: .5rem;

  @media (max-width: 875px) {
    width: 100%;
  }

  @media (max-width: 500px) {
    gap: .25rem;
  }
`

const Link = styled.a`
  display: flex;
  text-align: center;
  align-items: center;
  margin: .5em 0;
  padding: .5em;
  justify-content: center;
  width: 20%;
  background-color: ${(props) => props.theme.primaryColor};
  color:${(props) => props.theme.backgroundColor};
  border: 1px solid ${(props) => props.theme.primaryColor};
  transition: background-color 0.1s ease, color 0.1s ease;
  text-decoration: none;

  @media (max-width: 500px) {
    font-size: .75em;
  }

  :hover {
    cursor: crosshair;
    background-color: ${(props) => props.theme.hoverColor};
  }

  :focus {
    outline: 1px solid ${(props) => props.theme.hoverColor};
  }
`

const Button = styled(DefaultButton)`
  display: flex;
  text-align: center;
  align-items: center;
  margin: .5em 0;
  padding: .5em;
  justify-content: center;
  width: 20%;
  background-color: ${(props) => props.theme.primaryColor};
  color: ${(props) => props.theme.backgroundColor};
  border: 1px solid ${(props) => props.theme.primaryColor};

  @media (max-width: 500px) {
    font-size: .75em;
  }

  :active {
    filter: blur(.02em);
  }
`

const ToggleButton = styled(Button)`
  background-color: ${(props) => props.warden ?  props.theme.backgroundColor : props.theme.highlightColor};
  border: 1px solid ${(props) => props.warden ? props.theme.primaryColor : props.theme.hoverColor};
  color: ${(props) => props.warden ?  props.theme.highlightColor : props.theme.backgroundColor};

  :hover {
    color: ${(props) => props.theme.backgroundColor};
  }
`

const Spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Downloader = styled.div`
  border: 6px solid ${(props) => props.theme.primaryColor};
  border-top: 6px solid ${(props) => props.theme.backgroundColor};
  border-radius: 50%;
  width: .5em;
  height: .5em;
  animation: ${Spin} 1s linear infinite;
`

const Footer = styled.footer`
  border-top: 1px solid ${(props) => props.theme.primaryColor};
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: var(--max-width);
  padding: 0 var(--main-padding);
  margin: 0 auto;

  @media (max-width: 1400px) {
    padding: 0 2em;
  }

  @media (max-width: 875px) {
    padding: 0 1em;
    display: block;
  }

  p {
    font-size: .85em;
    margin-top: .5em;
  }

  a {
    color: ${(props) => props.theme.primaryColor};

    :focus {
      outline: 1px solid ${(props) => props.theme.hoverColor};
    }
  }
`

const CreditBox = styled.div`
  display: block;
  text-align: ${(props) => props.align};

  @media (max-width: 875px) {
    text-align: left;
  }
`

const SecretRow = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
`

const GameBoy = styled.button`
  font-weight: bold;
  font-size: .75rem;
  margin: 0;
  background-color: ${(props) => props.theme.backgroundColor};
  color: ${(props) => props.theme.primaryColor};
  border: none;

  :hover {
    cursor: crosshair;
    background-color: ${(props) => props.theme.hoverColor};
  }

  :focus {
    outline: 1px solid ${(props) => props.theme.hoverColor};
  }
`

const BlockadeButton = styled.button`
  width: 1em;
  margin: .25em;
  background-color: ${(props) => props.theme.backgroundColor};
  color: ${(props) => props.theme.primaryColor};

  :hover {
    cursor: crosshair;
    background-color: ${(props) => props.theme.hoverColor};
  }

  :focus {
    outline: 1px solid ${(props) => props.theme.hoverColor};
  }
`

const StyledBlockadeIcon = styled(blockadeIcon)`
  fill: ${(props) => props.theme.primaryColor};
`

const MainDisplay = styled.main`
  display: flex;
  flex-direction: row-reverse;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding: 1em var(--main-padding);
  max-width: var(--max-width);
  margin: 0 auto;

  @media (max-width: 1400px) {
    padding: 1em 2em;
  }

  @media (max-width: 875px) {
    display: block;
    padding: 1em;
  }
`

const ImageContainer = styled.section`
  width: 50%;
  margin-bottom: auto;

  @media (max-width: 875px) {
    width: 100%;
  }
`

const PlanetDetails = styled.section`
  display: block;
  width: 50%;
  margin-bottom: auto;

  @media (max-width: 875px) {
    width: 100%;
  }
`

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  border: 1px solid ${(props) => props.theme.primaryColor};
  padding: .25em .75em;
  background-color: ${(props) => props.theme.primaryColor};
  color: ${(props) => props.theme.backgroundColor};
`

const ClickHeading = styled(DefaultButton)`
  display: flex;
  justify-content: space-between;
  border: 1px solid ${(props) => props.theme.primaryColor};
  padding: .25em .75em;
  background-color: ${(props) => props.theme.primaryColor};
  color: ${(props) => props.theme.backgroundColor};
`

const PlanetHeading = styled(ClickHeading)`
  @media (max-width: 875px) {
    display: none;
  }
`

const MobileHeading = styled(ClickHeading)`
 display: none;
  @media (max-width: 875px) {
    display: flex;
  }
`

const DisplaySection = styled.div`
  border: 1px solid ${(props) => props.theme.primaryColor};
  margin-bottom: 1rem;
`

const Layout = ({ newPlanet, downloading, setDownloading, wardenView, setWardenView,
  currentPlanet, cycleColor, theme, setGameBoy, rerollWarden, rerollPoi, rerollName, rerollData, setBlockadeStatus, blockade }) => {

  const screenshot = useRef()
  const { planetName, planetData, scanResults, placesOfInterest, wardenInfo, gaseous, imageValues } = currentPlanet
  const { name, number } = planetName
  const { planetViewbox, planetColors, starNum, poiNums, textureNum, orbitalTag, hasWater } = imageValues

  const captureElement = async (element) => {
    setDownloading(true)
    const options = {
      backgroundColor: theme.backgroundColor,
      imageTimeout: 0
    }
    const canvas = await html2canvas(element, options)
    const dataURL = canvas.toDataURL('image/png')
    const fileName = `${wardenView ? `[WARDEN ONLY] - `: `` }${_.toUpper(name)} ${_.toUpper(number)}`
    downloadjs(dataURL, `${fileName}`, 'image/png')
    setDownloading(false)
  }
  return (
    <LayoutContainer>
      <Header>
        <HeaderContainer>
          <TitleContainer>
            <MainTitle>Rimspace Planet Generator</MainTitle>
            <VersionButton href="https://anodyneprintware.com/planets/changelog/" target='_blank' rel='noreferrer'>v1.1</VersionButton>
          </TitleContainer>
          <ButtonContainer>
            <Button onClick={() => newPlanet(theme)}>New Planet</Button>
            <ToggleButton warden={wardenView} onClick={() => setWardenView(wardenView)}>{wardenView ? 'Warden View ' : 'Player View'}</ToggleButton>
            <Button
              disabled={downloading}
              onClick={() => captureElement(screenshot.current)}>{downloading ? <Downloader /> : 'Download'}
            </Button>
            <Button onClick={cycleColor}>UI Color</Button>
            <Link href='https://anodyneprintware.com/' target='_blank' rel='noreferrer'>Home</Link>
          </ButtonContainer>
        </HeaderContainer>
      </Header>
      <MainDisplay ref={screenshot}>
        <ImageContainer>
          <MobileHeading onClick={rerollName}>
            <h2>{_.toUpper(name)}</h2>
            <h2>{_.toUpper(number)}</h2>
          </MobileHeading>
          <PlanetImage
            planetViewbox={planetViewbox}
            planetColors={planetColors}
            starNum={starNum}
            poiNums={poiNums}
            orbitalTag={orbitalTag}
            textureNum={textureNum}
            gaseous={gaseous}
            hasWater={hasWater}
            blockade={blockade}
          />
          <SystemMonitor />
        </ImageContainer>
        <PlanetDetails>
          <PlanetHeading onClick={rerollName}>
            <h2>{_.toUpper(name)}</h2>
            <h2>{_.toUpper(number)}</h2>
          </PlanetHeading>
          {blockade ?
            (
              <BlockadeText />
            ) :
            (
              <>
                <DisplaySection>
                  <TypeRow gaseous={gaseous} />
                  <DataRow data={planetData.size} reroll={rerollData} type={"size"}/>
                  <DataRow data={planetData.temperature} reroll={rerollData} type={"temperature"} />
                  <DataRow data={planetData.gravity} reroll={rerollData} type={"gravity"} />
                  <DataRow data={planetData.atmosphere} reroll={rerollData} type={"atmosphere"} />
                  <ScanReadout
                    diameter={scanResults.diameter}
                    temperature={scanResults.avgTemp}
                    gravity={scanResults.surfaceGravity}
                    commonElements={scanResults.commonElements}
                    rareElement={scanResults.rareElement}
                    hoursPerDay={scanResults.hoursPerDay}
                    reroll={rerollData}
                  />
                  <Scanner data={scanResults.graphData} />
                </DisplaySection>
                <Heading>
                  <h2>Points of Interest</h2>
                </Heading>
                <DisplaySection>
                  <>
                    <PoiRow reroll={rerollPoi} poi={placesOfInterest.orbital} number={'one'} wardenView={wardenView} />
                    {!gaseous &&
                      <>
                        <PoiRow reroll={rerollPoi} poi={placesOfInterest.poi1} number={'two'} wardenView={wardenView} />
                        <PoiRow reroll={rerollPoi} poi={placesOfInterest.poi2} number={'three'} wardenView={wardenView} />
                      </>
                    }
                  </>
                </DisplaySection>
                <DisplaySection>
                  {wardenView ?
                    (
                      <>
                        <Heading>
                          <h2>Planetary Database (access granted)</h2>
                        </Heading>
                        <WardenRow reroll={rerollWarden} type={wardenInfo.feature.tag} description={wardenInfo.feature.description}></WardenRow>
                        <WardenRow reroll={rerollWarden} type={wardenInfo.hook.tag} description={wardenInfo.hook.description}></WardenRow>
                        <WardenRow reroll={rerollWarden} type={wardenInfo.mystery.tag} description={wardenInfo.mystery.description}></WardenRow>
                      </>
                    ) : (
                      <>
                        <Heading>
                          <h2>Planetary Database (offline)</h2>
                        </Heading>
                        <HiddenRow text={wardenInfo.random[0]} />
                        <HiddenRow text={wardenInfo.random[1]} />
                        <HiddenRow text={wardenInfo.random[2]} />
                      </>
                    )
                  }
                </DisplaySection>
              </>
            )
          }
        </PlanetDetails>
      </MainDisplay>
      <Footer>
        <FooterContainer>
          <CreditBox align={"left"}>
            <p><a href="https://anodyneprintware.com/planets/changelog/" target='_blank' rel='noreferrer'>v 1.1 - scanning enhanced</a></p>
            <p>Created for <a href="https://www.kickstarter.com/projects/yusem/hull-breach-vol-1" target='_blank' rel='noreferrer'>Hull Breach Vol. 1</a> </p>
            <p>Inspired by the <a href= "https://www.mothershiprpg.com/" target='_blank' rel='noreferrer'>Mothership Sci-Fi Horror RPG</a></p>
          </CreditBox>
          <CreditBox align={"center"}>
            <p>Web app by <a href="https://www.mattumland.dev" target='_blank' rel='noreferrer'>Matt Umland</a></p>
            <p>Content development by Ian Yusem</p>
            <p>Anodyne Printware ©2023</p>
          </CreditBox>
          <CreditBox align={"right"}>
            <p>Planet graphics by <a href="https://junkgolem.itch.io/" target='_blank' rel='noreferrer'>Meredith Silver</a></p>
            <p>Additional content by <a href="https://www.toatabletop.com" target='_blank' rel='noreferrer'>Liam Stevens</a> & <a href="https://www.traaa.sh/" target='_blank' rel='noreferrer'>Quadra</a></p>
            <SecretRow>
              <GameBoy onClick={setGameBoy}>[+ ..]</GameBoy>
              <BlockadeButton onClick={setBlockadeStatus}>
                <StyledBlockadeIcon />
              </BlockadeButton>
            </SecretRow>
          </CreditBox>
        </FooterContainer>
      </Footer>
    </LayoutContainer>
  )
}

export default Layout;
